import { defineStore } from 'pinia'
import { getSceneBgSrc } from '@/api/scene'
import client from '@/api/client'
import { nextTick } from 'vue'
import { getDefaultLens } from '@/includes/lens'

const sortLenses = lenses => {
  return lenses.sort((a, b) => {
    const focalLengthA = a.focalLength ? parseFloat(a.focalLength.replace('mm', '')) : 0
    const focalLengthB = b.focalLength ? parseFloat(b.focalLength.replace('mm', '')) : 0
    const transmissionA = a.transmission
      ? parseFloat(a.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const transmissionB = b.transmission
      ? parseFloat(b.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const focalDiff = focalLengthA - focalLengthB
    if (focalDiff !== 0) {
      return focalDiff
    }
    return transmissionA - transmissionB
  })
}

const getSceneImage = (item, sceneType) => {
  if (!item) return null
  const scenesMap = {
    desert: item.sceneImage1,
    night: item.sceneImage2,
    interior: item.sceneImage3
  }
  return scenesMap[sceneType.toLowerCase()]
}

export const useScenesStore = defineStore('scenes', {
  state: () => ({
    base_URL: process.env.VUE_APP_BASE_URL,
    data: [],
    datafetched: false,
    lensesName: [],
    LeftSelectedVideoId: null,
    RightSelectedVideoId: null,
    defaultLeftVideoId: null,
    defaultRightVideoId: null,
    defaultMobileVideoId: null,
    LeftSelectedVideoDetails: [],
    RightSelectedVideoDetails: [],
    SelectedVideo: null,
    LeftFocalLength: null,
    RightFocalLength: null,
    LeftTransmission: null,
    RightTransmission: null,
    LeftVideoLoaded: false,
    RightVideoLoaded: false,
    focalLengthOptionsLeft: [],
    focalLengthOptionsRight: [],
    transmissionsLeft: [],
    transmissionsRight: [],
    videoControlBtn: 'play',
    playVideo: false,
    pauseVideo: false,
    isPremiumUser: false,
    sceneType: 'desert',
    //states for mobile version selected video
    SelectedVideoDetails: null,
    SelectedFocalLength: null,
    SelectedTransmission: null,
    debounceTimer: null,
    searchIsActive: false,
    activeSide: 'LEFT',
    isProgrammaticScroll: false,
    programmaticScrollTimer: null
  }),
  getters: {
    getLeftSelectedVideoId: state => state.LeftSelectedVideoId,
    getRightSelectedVideoId: state => state.RightSelectedVideoId,
    getLeftFocalLength: state => state.LeftFocalLength,
    getRightFocalLength: state => state.RightFocalLength,
    getleftTransmission: state => state.LeftTransmission,
    getRightTransmission: state => state.RightTransmission,
    getLeftVideoLoaded: state => state.LeftVideoLoaded,
    getRightVideoLoaded: state => state.RightVideoLoaded,
    getLensesdata: state => state.data,
    getFilteredLenses: state => state.filteredLenses
  },
  actions: {
    async fetchData() {
      try {
        const API_URL = `${this.base_URL}/lens/scenes?sceneType=${this.sceneType.toUpperCase()}`
        const response = await client.get(API_URL)
        this.data = response?.data || []
        this.isPremiumUser = response.data?.isPremiumUser
        this.setLensesName()
        this.setDataFetched()
        this.setDefaultLeftVideo()
        this.setDefaultRightVideo()
        this.setDefaultMobileVideo()
        this.setLensesName()
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    setDataFetched() {
      this.datafetched = true
    },
    setActiveSide(side) {
      this.activeSide = side
    },

    setLensesName() {
      if (this.data) {
        // Conversion logic
        const uniqueLensArray = this.data.map(category => {
          const uniqueLensNames = new Set()
          const lenses = category.lenses
            .filter(lens => {
              if (uniqueLensNames.has(lens.name)) {
                return false
              }
              uniqueLensNames.add(lens.name)
              return true
            })
            .map(lens => ({
              name: lens.name
            }))

          return {
            category: category.name,
            lenses: lenses
          }
        })

        this.lensesName = uniqueLensArray
      }
    },
    setDefaultLeftVideo() {
      this.defaultLeftVideoId = getDefaultLens('Left', this.data)
    },
    setDefaultRightVideo() {
      this.defaultRightVideoId = getDefaultLens('Right', this.data)
    },
    setDefaultMobileVideo() {
      this.defaultMobileVideoId = getDefaultLens(null, this.data)
    },
    setLeftSelectedVideoId(LeftVideoID) {
      this.LeftSelectedVideoId = LeftVideoID
    },
    setRightSelectedVideoId(RightVideoID) {
      this.RightSelectedVideoId = RightVideoID
    },
    setLeftFocalLength(LeftFocalLength) {
      this.LeftFocalLength = LeftFocalLength
    },
    setRightFocalLength(RightFocalLength) {
      this.RightFocalLength = RightFocalLength
    },
    setLeftTransmission(LeftTransmission) {
      this.LeftTransmission = LeftTransmission
    },
    setRightTransmission(RightTransmission) {
      this.RightTransmission = RightTransmission
    },
    toggleLeftVideoLoaded() {
      this.LeftVideoLoaded = !this.LeftVideoLoaded
    },
    toggleRightVideoLoaded() {
      this.RightVideoLoaded = !this.RightVideoLoaded
    },
    setSelectedVideo(lenseName) {
      this.SelectedVideo = lenseName
    },
    setSelectedFocalLength(focalLength) {
      this.SelectedFocalLength = focalLength
    },
    setSelectedTransmission(transmission) {
      this.SelectedTransmission = transmission
    },
    setSceneType(scene) {
      this.sceneType = scene
    },
    setLeftSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa => fa.name.toLowerCase().replace(/\s+/g, '-') + '-' + 'left' === this.LeftSelectedVideoId
      )

      this.LeftSelectedVideoDetails = sortLenses(middleObject)
    },
    setRightSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa =>
          fa.name.toLowerCase().replace(/\s+/g, '-') + '-' + 'right' === this.RightSelectedVideoId
      )
      this.RightSelectedVideoDetails = sortLenses(middleObject)
    },
    setSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa => fa.name.toLowerCase().replace(/\s+/g, '-') === this.SelectedVideo
      )
      this.SelectedVideoDetails = sortLenses(middleObject)
    },
    generateVideoLabelText(label, target) {
      const labelLeft =
        this.LeftSelectedVideoDetails.length > 0 ? this.LeftSelectedVideoDetails[0]?.name : 'n/a'
      const labelRight =
        this.RightSelectedVideoDetails.length > 0 ? this.RightSelectedVideoDetails[0]?.name : 'n/a'

      label.value.textContent = target === 'LEFT' ? labelLeft : labelRight
    },
    removeDuplicates(arr) {
      return arr.filter((item, index, self) => {
        return index === self.findIndex(t => t.focalLength === item.focalLength)
      })
    },
    generateTransmissionDropdown(array, focalLength) {
      return array.filter(transmission => transmission.focalLength === focalLength)
    },
    async generateDropDown(target, focalLengthElement, transmissionElement) {
      try {
        const tempArray =
          target === 'LEFT'
            ? this.LeftSelectedVideoDetails
            : target === 'RIGHT'
              ? this.RightSelectedVideoDetails
              : this.SelectedVideoDetails

        const pinnedFocalLength =
          target === 'LEFT'
            ? this.LeftFocalLength
            : target === 'RIGHT'
              ? this.RightFocalLength
              : this.SelectedFocalLength

        let focalLengths = await this.removeDuplicates(tempArray)

        // Keep whichever focal length is actually selected (e.g. pinned by a
        // keyword search) instead of always building the dropdown around the
        // first variant in the list
        const activeFocalLength = focalLengths.some(
          option => option.focalLength === pinnedFocalLength
        )
          ? pinnedFocalLength
          : focalLengths[0].focalLength

        const transmission = await this.generateTransmissionDropdown(tempArray, activeFocalLength)

        // Replace (rather than append to) the option lists driving the v-for -
        // pushing onto them left every previous selection's options in the DOM
        if (target === 'LEFT') {
          this.focalLengthOptionsLeft = focalLengths.map(option => option.focalLength)
          this.transmissionsLeft = transmission.map(option => option.transmission)
        } else {
          this.focalLengthOptionsRight = focalLengths.map(option => option.focalLength)
          this.transmissionsRight = transmission.map(option => option.transmission)
        }

        focalLengthElement.value.style.display = ''
        transmissionElement.value.style.display = ''

        const pinnedTransmission =
          target === 'LEFT'
            ? this.LeftTransmission
            : target === 'RIGHT'
              ? this.RightTransmission
              : this.SelectedTransmission
        const activeTransmission = transmission.some(
          option => option.transmission === pinnedTransmission
        )
          ? pinnedTransmission
          : transmission[0]?.transmission

        // Wait for Vue to render the <option> elements from the arrays above
        // before setting .value - otherwise the select still has no options
        // and the browser silently falls back to selecting the first one
        await nextTick()
        focalLengthElement.value.value = activeFocalLength
        transmissionElement.value.value = activeTransmission
      } catch (err) {
        console.log(err)
      }
    },
    generateIcon(icon, iconElem) {
      if (!icon || !iconElem) return
      iconElem.value.src = icon
      iconElem.value.style.display = 'block'
    },
    loadDefaultVideoPath(videoRef, target) {
      // spinnerRef.value.style.display = "flex";
      // const sourceElement = videoRef.value.querySelector("source");
      const items =
        target === 'LEFT'
          ? this.LeftSelectedVideoDetails
          : target === 'RIGHT'
            ? this.RightSelectedVideoDetails
            : this.SelectedVideoDetails

      let sourceItem = []

      if (target === 'LEFT') {
        sourceItem = items.filter(item => {
          return (
            item.focalLength === this.LeftFocalLength && item.transmission === this.LeftTransmission
          )
        })
      } else if (target === 'RIGHT') {
        sourceItem = items.filter(item => {
          return (
            item.focalLength === this.RightFocalLength &&
            item.transmission === this.RightTransmission
          )
        })
      }

      const source = getSceneImage(sourceItem[0], this.sceneType)
      // sourceElement.src = item[0].videoPath;
      // Add event listener for load event to add the show class
      videoRef.value.addEventListener('load', function () {
        videoRef.value.classList.add('show')
      })
      videoRef.value.src = source || require('../assets/img/no_scene_available.jpg')

      // const posterImage = new Image();
      // posterImage.src = item[0].preview; // Specify the path to your poster image
      // posterImage.onload = () => {
      //   // Poster image has fully loaded and displayed, hide the spinner
      //   if (spinnerRef.value) spinnerRef.value.style.display = "none";
      // };

      // videoRef.value.load();
    },
    getCategoryId(lensId) {
      let categoryId = null

      for (const category of this.data) {
        for (const lens of category.lenses) {
          if (lens.id === lensId) {
            categoryId = category.id
            break
          }
        }
        if (categoryId) {
          break
        }
      }

      return categoryId
    },
    async loadMobileScenes(lensSceneRef, backgroundSceneRef) {
      const items = this.SelectedVideoDetails

      let sourceItem = []

      sourceItem = items.filter(item => {
        return (
          item.focalLength === this.SelectedFocalLength &&
          item.transmission === this.SelectedTransmission
        )
      })
      const categoryId = this.getCategoryId(sourceItem[0].id)
      const focalLength = this.SelectedFocalLength
      const source = getSceneImage(sourceItem[0], this.sceneType)

      const response = await getSceneBgSrc(categoryId, focalLength, this.sceneType.toUpperCase())

      let bgSource
      if (response) {
        bgSource = response?.data?.imageUrl
      }

      // sourceElement.src = item[0].videoPath;
      // Add event listener for load event to add the show class
      lensSceneRef.value.addEventListener('load', function () {
        lensSceneRef.value.classList.add('show')
      })
      lensSceneRef.value.src = source || require('../assets/img/no_scene_available.jpg')

      backgroundSceneRef.value.addEventListener('load', function () {
        backgroundSceneRef.value.classList.add('show')
      })
      backgroundSceneRef.value.src = bgSource || require('../assets/img/no_scene_available.jpg')
    },

    debounce(callback, time) {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = window.setTimeout(callback, time)
    },
    getElementLayoutTop(element, container) {
      let elementTop = 0
      let current = element
      while (current) {
        elementTop += current.offsetTop || 0
        current = current.offsetParent
      }

      let containerTop = 0
      current = container
      while (current) {
        containerTop += current.offsetTop || 0
        current = current.offsetParent
      }

      return elementTop - containerTop
    },
    snapToClosestOption(element, isMobile) {
      if (!element) return
      const viewportList = element
      const viewportFromTop = viewportList.getBoundingClientRect().top

      const marginTopOfViewPort =
        parseInt(
          window.getComputedStyle(viewportList).getPropertyValue('margin-top').replace('px', '')
        ) || 0

      const listItemsLink = viewportList.querySelectorAll('.nav-link')
      const viewportListHeightHalf = viewportList.getBoundingClientRect().height / 2
      const viewportListCenter = viewportFromTop + viewportListHeightHalf - marginTopOfViewPort + 6 // 6 is added as the padding and margin of the list assuming it is static

      // 3d effect (always updated for visual feedback)
      this.scroll3dEffect(viewportListCenter, isMobile)

      // Prevent snap selection change during programmatic scroll to prevent feedback loop
      if (this.isProgrammaticScroll) {
        return
      }

      const listItemsLi = []
      listItemsLink.forEach(link => {
        if (link.closest('.nav-item')) {
          listItemsLi.push(link.closest('.nav-item'))
        }
      })

      if (listItemsLi.length === 0) return

      // Use layout-based calculations relative to container's scroll top and client height
      const layoutCenter = viewportList.scrollTop + viewportList.clientHeight / 2
      let closestLi = listItemsLi[0]
      let firstItemLayoutTop = this.getElementLayoutTop(closestLi, viewportList)
      let closestDiff = Math.abs(layoutCenter - (firstItemLayoutTop + closestLi.clientHeight / 2))

      listItemsLi.forEach(item => {
        const itemLayoutTop = this.getElementLayoutTop(item, viewportList)
        const itemCenter = itemLayoutTop + item.clientHeight / 2
        const currentDiff = Math.abs(layoutCenter - itemCenter)

        // remove active class
        item.classList.remove('active-li')

        if (currentDiff < closestDiff) {
          closestDiff = currentDiff
          closestLi = item
        }
      })

      let defaultLeft, defaultRight, defaultVideo
      const isLeftOrRightList =
        viewportList.getAttribute('id') === 'viewportListLeft' ||
        viewportList.getAttribute('id') === 'viewportListRight'

      if (isLeftOrRightList || !isMobile) {
        defaultLeft = document.getElementById(this.defaultLeftVideoId)
        defaultRight = document.getElementById(this.defaultRightVideoId)
      } else {
        defaultVideo = document.getElementById(this.defaultMobileVideoId)
      }

      if (isLeftOrRightList) {
        if (viewportList.getAttribute('id') === 'viewportListLeft') {
          defaultLeft?.classList.remove('active-li')
        } else {
          defaultRight?.classList.remove('active-li')
        }
      } else if (!isMobile) {
        defaultLeft?.classList.remove('active-li')
        defaultRight?.classList.remove('active-li')
      } else {
        defaultVideo?.classList.remove('active-li')
      }

      //adds the active class where the font is bold
      closestLi.classList.add('active-li')
      const anchorElem = closestLi.querySelector('a')

      //check if the anchor text is already clicked
      if (anchorElem.getAttribute('data-clicked') === 'true') {
        return
      } else {
        // store.setLeftSelectedVideoId(anchorElem.getAttribute('id'))
        this.debounce(() => {
          if (isLeftOrRightList || !isMobile) {
            viewportList.getAttribute('id') === 'viewportListLeft'
              ? this.setLeftSelectedVideoId(anchorElem.getAttribute('id'))
              : this.setRightSelectedVideoId(anchorElem.getAttribute('id'))
          } else {
            this.setSelectedVideo(anchorElem.getAttribute('id'))
          }
        }, 700)
      }
    },
    scrollToItem(item) {
      if (!item) return
      this.isProgrammaticScroll = true

      const li = item.parentElement
      const scrollableList = li.parentElement.parentElement.parentElement

      // Immediately highlight target and un-highlight others to avoid lag/flicker
      scrollableList.querySelectorAll('.nav-item').forEach(el => {
        el.classList.remove('active-li')
      })
      li.classList.add('active-li')

      // Use layout-based offset calculations to center the item precisely, unaffected by 3D transforms
      const itemLayoutTop = this.getElementLayoutTop(li, scrollableList)
      const scrollTo = itemLayoutTop - scrollableList.clientHeight / 2 + li.clientHeight / 2

      scrollableList.scrollTo({
        top: scrollTo,
        behavior: 'smooth'
      })

      window.clearTimeout(this.programmaticScrollTimer)
      this.programmaticScrollTimer = window.setTimeout(() => {
        this.isProgrammaticScroll = false
      }, 1000)
    },
    addHeightOntopAndBottom(element) {
      const viewportList = element.value
      if (!viewportList) {
        return
      }
      const lists = viewportList.querySelectorAll('li')
      const clientHeight = viewportList.clientHeight
      const firstLink = document.querySelectorAll('.nav-link')[0]
      const lastListHeight = lists[lists.length - 1].clientHeight

      const firstNavTitle = document.querySelectorAll('.nav-title')[0]

      // is mobile
      if (viewportList.classList.contains('viewport')) {
        // substracting the paddings and margins of the nested uls and lis assuming the structure and the values wont change
        viewportList.style.paddingTop = `${
          clientHeight / 2 -
          2 -
          firstNavTitle.clientHeight -
          2 -
          4 -
          2 - // 16 for the margin top of the nav-title added on css
          firstLink.clientHeight / 2
        }px`
      } else {
        viewportList.style.paddingTop = `${
          clientHeight / 2 -
          2 -
          firstNavTitle.clientHeight -
          2 -
          4 -
          2 -
          10 - // 10 for the margin top of the nav-title added on css
          firstLink.clientHeight / 2
        }px`
      }

      // substracting the paddings and margins of the nested uls and lis assuming the structure and the values wont change
      viewportList.style.paddingBottom = `${clientHeight / 2 - 2 - 2 - 4 - lastListHeight / 2}px`
    },
    scroll3dEffect(viewportListCenter, isMobile) {
      const navEntities = document.querySelectorAll('.nav-entity')

      navEntities.forEach(item => {
        let base = Math.min(item.getBoundingClientRect().top - viewportListCenter, 180)

        base = Math.max(base, -180)

        item.style.transform = `rotateX(${base * 0.6}deg)`
        if (base > 0) {
          item.style.transformOrigin = 'top'
        } else {
          item.style.transformOrigin = 'bottom'
        }
        if (isMobile) {
          item.style.opacity = 1 - Math.abs(base) * 0.01
        }
      })
    },
    setVideoPathOnChange(lensesItem, videoRef) {
      if (!lensesItem) return

      const source = getSceneImage(lensesItem, this.sceneType)
      // Add event listener for load event to add the show class
      videoRef.value.addEventListener('load', function () {
        videoRef.value.classList.add('show')
      })
      videoRef.value.src = source || require('../assets/img/no_scene_available.jpg')
      // const sourceElement = videoRef.value.querySelector("source");
      // sourceElement.src = lensesItem.videoPath;
      // videoRef.value.load();
    }
  }
})
